import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar,
  Plus,
  Trash2,
  CalendarDays,
  CalendarOff,
} from "lucide-react";

const CalendarsPage = () => {
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventType, setEventType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isCalendarDialogOpen, setIsCalendarDialogOpen] = useState(false);
  const [isExceptionDialogOpen, setIsExceptionDialogOpen] = useState(false);

  const [calendarForm, setCalendarForm] = useState({
    name: "",
    work_days: [1, 2, 3, 4, 5],
    work_hours_start: "07:00",
    work_hours_end: "17:00",
  });

  const [exceptionForm, setExceptionForm] = useState({
    date_start: "",
    date_end: "",
    exception_type: "holiday",
    name: "",
  });

  useEffect(() => {
    fetchCalendars();
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedCalendar) {
      fetchExceptions();
    }
  }, [selectedCalendar]);

  useEffect(() => {
    fetchEvents();
  }, [eventType]);

  const fetchCalendars = async () => {
    try {
      const response = await axios.get(`${API}/calendars`);
      setCalendars(response.data);
      if (response.data.length > 0 && !selectedCalendar) {
        setSelectedCalendar(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to load calendars");
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const params = new URLSearchParams({ days: "90" });
      if (eventType !== "all") params.set("event_type", eventType);
      const response = await axios.get(`${API}/calendars/events?${params.toString()}`);
      setEvents(response.data.events || []);
    } catch (error) {
      toast.error("Failed to load calendar events");
    }
  };

  const fetchExceptions = async () => {
    try {
      const response = await axios.get(
        `${API}/calendars/${selectedCalendar}/exceptions`
      );
      setExceptions(response.data);
    } catch (error) {
      toast.error("Failed to load exceptions");
    }
  };

  const handleCreateCalendar = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/calendars`, calendarForm);
      setCalendars([...calendars, response.data]);
      fetchEvents();
      setIsCalendarDialogOpen(false);
      setCalendarForm({
        name: "",
        work_days: [1, 2, 3, 4, 5],
        work_hours_start: "07:00",
        work_hours_end: "17:00",
      });
      toast.success("Calendar created");
    } catch (error) {
      toast.error("Failed to create calendar");
    }
  };

  const handleCreateException = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/calendars/${selectedCalendar}/exceptions`,
        exceptionForm
      );
      setExceptions([...exceptions, response.data]);
      setIsExceptionDialogOpen(false);
      fetchEvents();
      setExceptionForm({
        date_start: "",
        date_end: "",
        exception_type: "holiday",
        name: "",
      });
      toast.success("Exception added");
    } catch (error) {
      toast.error("Failed to add exception");
    }
  };

  const handleDeleteException = async (exceptionId) => {
    try {
      await axios.delete(
        `${API}/calendars/${selectedCalendar}/exceptions/${exceptionId}`
      );
      setExceptions(exceptions.filter((e) => e.id !== exceptionId));
      fetchEvents();
      toast.success("Exception deleted");
    } catch (error) {
      toast.error("Failed to delete exception");
    }
  };

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const toggleWorkDay = (day) => {
    if (calendarForm.work_days.includes(day)) {
      setCalendarForm({
        ...calendarForm,
        work_days: calendarForm.work_days.filter((d) => d !== day),
      });
    } else {
      setCalendarForm({
        ...calendarForm,
        work_days: [...calendarForm.work_days, day].sort(),
      });
    }
  };

  const selectedCalendarData = calendars.find((c) => c.id === selectedCalendar);
  const eventTypeLabels = {
    all: "All Events",
    bid: "Bids",
    schedule: "Schedule",
    procurement: "Procurement",
    document: "Documents",
    approval: "Approvals",
    exception: "Exceptions",
  };
  const eventColor = (type) => ({
    bid: "text-blue-400",
    schedule: "text-emerald-400",
    procurement: "text-purple-400",
    document: "text-amber-400",
    approval: "text-red-400",
    exception: "text-slate-300",
  }[type] || "text-muted-foreground");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary text-xl">
          Loading calendars...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendars-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">
            Calendars
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure work schedules and holidays
          </p>
        </div>
        <Dialog open={isCalendarDialogOpen} onOpenChange={setIsCalendarDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" />
              New Calendar
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card">
            <DialogHeader>
              <DialogTitle>Create Calendar</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCalendar} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={calendarForm.name}
                  onChange={(e) =>
                    setCalendarForm({ ...calendarForm, name: e.target.value })
                  }
                  placeholder="Standard Work Week"
                  required
                  className="bg-muted/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Work Days</Label>
                <div className="flex gap-2">
                  {dayNames.map((day, index) => (
                    <Button
                      key={day}
                      type="button"
                      variant={
                        calendarForm.work_days.includes(index)
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => toggleWorkDay(index)}
                      className={
                        calendarForm.work_days.includes(index)
                          ? "bg-primary"
                          : ""
                      }
                    >
                      {day}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={calendarForm.work_hours_start}
                    onChange={(e) =>
                      setCalendarForm({
                        ...calendarForm,
                        work_hours_start: e.target.value,
                      })
                    }
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={calendarForm.work_hours_end}
                    onChange={(e) =>
                      setCalendarForm({
                        ...calendarForm,
                        work_hours_end: e.target.value,
                      })
                    }
                    className="bg-muted/50"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCalendarDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary">
                  Create
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Calendar Selector */}
      <Card className="stat-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label>Select Calendar</Label>
              <Select value={selectedCalendar} onValueChange={setSelectedCalendar}>
                <SelectTrigger className="bg-muted/50 mt-1">
                  <SelectValue placeholder="Select a calendar" />
                </SelectTrigger>
                <SelectContent>
                  {calendars.map((cal) => (
                    <SelectItem key={cal.id} value={cal.id}>
                      {cal.name} {cal.is_default && "(Default)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedCalendarData && (
              <div className="text-sm text-muted-foreground">
                <p>
                  Work Hours: {selectedCalendarData.work_hours_start} -{" "}
                  {selectedCalendarData.work_hours_end}
                </p>
                <p>
                  Work Days:{" "}
                  {selectedCalendarData.work_days
                    .map((d) => dayNames[d])
                    .join(", ")}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Unified Calendar Agenda */}
      <Card className="stat-card">
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2 font-heading">
            <Calendar className="h-5 w-5 text-primary" />
            Unified 90-Day Agenda
          </CardTitle>
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger className="bg-muted/50 w-[190px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(eventTypeLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {events.length > 0 ? (
            <div className="space-y-2">
              {events.map((event) => (
                <div key={`${event.type}-${event.id}`} className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-3 bg-muted/30 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold uppercase ${eventColor(event.type)}`}>{event.type}</span>
                      {event.severity && event.severity !== "normal" && <span className="text-xs text-red-400 uppercase">{event.severity}</span>}
                      <p className="font-medium">{event.title}</p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {event.project_name || event.source} • {event.status?.replace(/_/g, " ") || "scheduled"}
                    </p>
                  </div>
                  <div className="text-sm text-muted-foreground md:text-right">
                    {event.start ? new Date(event.start).toLocaleDateString() : "No date"}
                    {event.end && event.end !== event.start ? ` - ${new Date(event.end).toLocaleDateString()}` : ""}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No calendar events found for this view.</p>
          )}
        </CardContent>
      </Card>

      {/* Exceptions */}
      {selectedCalendar && (
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 font-heading">
              <CalendarOff className="h-5 w-5 text-amber-500" />
              Exceptions (Holidays & Shutdowns)
            </CardTitle>
            <Dialog
              open={isExceptionDialogOpen}
              onOpenChange={setIsExceptionDialogOpen}
            >
              <DialogTrigger asChild>
                <Button size="sm" className="bg-primary hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Exception
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card">
                <DialogHeader>
                  <DialogTitle>Add Calendar Exception</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateException} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={exceptionForm.name}
                      onChange={(e) =>
                        setExceptionForm({
                          ...exceptionForm,
                          name: e.target.value,
                        })
                      }
                      placeholder="Christmas Day"
                      required
                      className="bg-muted/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={exceptionForm.exception_type}
                      onValueChange={(value) =>
                        setExceptionForm({ ...exceptionForm, exception_type: value })
                      }
                    >
                      <SelectTrigger className="bg-muted/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="holiday">Holiday</SelectItem>
                        <SelectItem value="shutdown">Shutdown</SelectItem>
                        <SelectItem value="weather_hold">Weather Hold</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={exceptionForm.date_start}
                        onChange={(e) =>
                          setExceptionForm({
                            ...exceptionForm,
                            date_start: e.target.value,
                          })
                        }
                        required
                        className="bg-muted/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={exceptionForm.date_end}
                        onChange={(e) =>
                          setExceptionForm({
                            ...exceptionForm,
                            date_end: e.target.value,
                          })
                        }
                        required
                        className="bg-muted/50"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsExceptionDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary">
                      Add
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {exceptions.length > 0 ? (
              <div className="space-y-2">
                {exceptions.map((exc) => (
                  <div
                    key={exc.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <CalendarDays className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{exc.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {exc.date_start} to {exc.date_end} •{" "}
                          <span className="capitalize">{exc.exception_type}</span>
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteException(exc.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No exceptions configured for this calendar.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CalendarsPage;
